import React, { useState, useMemo, useEffect, useRef } from 'react';
import './AssetTagsList.css';
import { compareAssetTags } from '../utils/reconSort';

/*
 * AssetTagsList (shared)
 * ----------------------
 * Self-contained list / add / inline-edit UI for asset tags. Mirrors
 * PartNumbersList's ownership contract and styling approach:
 *   - Add-row ownership: host (controlled via newTag / setNewTag).
 *   - Edit-row ownership: component-internal. Only one row in edit mode at
 *     a time. Save calls onEdit(id, {tag_value}); the host is expected to
 *     await the backend write and may rethrow to keep edit mode open on
 *     error.
 *
 * AssetTags carries only `tag_value` — there is no tag_type field in the
 * backend schema (Phase 0 audit, 2026-05-29). Edit row exposes value only.
 *
 * Prop contract:
 *   assetTags   (array, required)
 *   loading     (bool, default false)
 *   error       (string, optional)
 *   readOnly    (bool, default false) — suppresses add/edit/delete chrome
 *   onAdd       (fn, required if !readOnly)
 *   onEdit      (fn(id, {tag_value}) — required if !readOnly)
 *   onDelete    (fn(id) — required if !readOnly)
 *   newTag / setNewTag — host-owned add-row state
 *   onEditingChange
 *               — (fn(bool) | null, default null) fired when the internal
 *                 edit row opens or closes. Reports THAT an edit is open,
 *                 never its contents. Note this component ALSO commits on
 *                 Enter, which PartNumbersList does not; that route lands
 *                 in cancelEdit like the others, so it reports correctly.
 *   pointerCapture
 *               — (bool, default false) routes this component's two COMMIT
 *                 buttons (edit-row Save, add-row Add) through
 *                 setPointerCapture on pointerdown, so pointerup still lands
 *                 on the button after layout moves it mid-gesture.
 *                 F-58: on iOS the keyboard dismisses when a button is
 *                 TOUCHED, the host dialog grows, and the button travels
 *                 down between pointerdown and liftoff — pointerup hits
 *                 empty space and NO click is dispatched at all. Witnessed
 *                 on TEST 8f514f5, 2026-09-02, iOS handset on the
 *                 part-number add row; this component's add row is the same
 *                 shape in the same dialog, so it carries the same defect.
 *                 ⛔ DEFAULT OFF IS LOAD-BEARING. onClick stays ungated (the
 *                 established F-58 shape — keyboard, programmatic and
 *                 assistive activation dispatch a click with no pointer
 *                 sequence), so where the button does NOT move, one
 *                 activation delivers BOTH a pointerup and a click. The
 *                 field modal absorbs that: updateChildSet is a full-set
 *                 replace from the same stale base. The three legacy screens
 *                 do NOT — their onAdd POSTs a mint, so a double-fire would
 *                 create TWO asset-tag documents. Only the field zone opts
 *                 in; every other call site must inherit the off path.
 *                 The Enter-key route at the edit input is unaffected: it is
 *                 a keydown, never a pointer sequence.
 */

export default function AssetTagsList({
  assetTags,
  loading = false,
  error,
  readOnly = false,
  onAdd,
  onEdit,
  onDelete,
  newTag,
  setNewTag,
  // F-44b: OPTIONAL and defaulted — 4 call sites, 3 of them legacy screens
  // that pass nothing here.
  onEditingChange = null,
  // F-58: OPTIONAL and defaulted OFF — same 4 call sites, and only the field
  // zone (FieldRecordModal) opts in. ToolDetailScreen, LifecycleDetailPanel
  // and DisposalDetailPanel pass nothing here and change by ZERO lines. The
  // direction of this default is the whole safety argument, not house style
  // — see the prop contract above.
  pointerCapture = false,
}) {
  const canEdit = !readOnly && typeof onEdit === 'function';

  // F-58 capture props for ONE commit button, or {} when opted out — the off
  // path adds no handler at all, so a legacy call site keeps its exact
  // current event surface. A factory rather than one inline pair because the
  // two commit buttons have DIFFERENT pointerup targets (the local saveEdit
  // vs the onAdd prop), and this keeps the opt-in check in one place instead
  // of two drifting copies. Mirrors PartNumbersList verbatim.
  // Release BEFORE firing; fn?.() because onAdd is a PROP and may be absent.
  const captureProps = (fn) => (pointerCapture ? {
    onPointerDown: (e) => {
      // Throws if the pointer is already released; nothing to do then.
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* already released */ }
    },
    onPointerUp: (e) => {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
      fn?.();
    },
  } : {});

  // Display-only sort: a sorted COPY for render. Edit/delete still fire by id
  // on the original tag ids, so stored order is never reordered.
  const sortedTags = useMemo(
    () => [...assetTags].sort(compareAssetTags), [assetTags]);

  const [editingId, setEditingId] = useState(null);
  const [editTag, setEditTag] = useState('');

  // Phase 3d: an edit row still open at UNMOUNT is reported closed — the same
  // fix, for the same reason, as PartNumbersList (see there). editOpenRef
  // tracks what was REPORTED; no callback, no call.
  const editOpenRef = useRef(false);
  const onEditingChangeRef = useRef(onEditingChange);
  onEditingChangeRef.current = onEditingChange;
  useEffect(() => () => {
    if (editOpenRef.current && onEditingChangeRef.current) onEditingChangeRef.current(false);
  }, []);

  function startEdit(tag) {
    if (!canEdit) return;
    setEditingId(tag.id);
    setEditTag(tag.tag_value || '');
    editOpenRef.current = true;
    if (onEditingChange) onEditingChange(true);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditTag('');
    // F-44b: covers the Cancel button, saveEdit's success path, AND the
    // Enter-key route at the edit input — all three funnel through here.
    editOpenRef.current = false;
    if (onEditingChange) onEditingChange(false);
  }

  async function saveEdit() {
    if (!canEdit || !editTag.trim()) return;
    try {
      await onEdit(editingId, { tag_value: editTag.trim() });
      cancelEdit();
    } catch {
      // Host surfaces the error via the `error` prop. Stay in edit mode so
      // the user can retry without re-typing.
    }
  }

  return (
    <>
      {error && <p className="atl-error">{error}</p>}
      <div className="atl-list">
        {loading && <p className="atl-loading">Loading asset tags...</p>}
        {!loading && assetTags.length === 0 && (
          <p className="atl-empty">No asset tags added.</p>
        )}
        {!loading && sortedTags.map(tag => {
          if (editingId === tag.id) {
            return (
              <div key={tag.id} className="atl-row atl-row-edit">
                <input
                  type="text"
                  className="atl-input atl-input-grow"
                  value={editTag}
                  onChange={e => setEditTag(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveEdit(); }}
                  autoFocus
                />
                <button
                  type="button"
                  className="atl-btn-save"
                  onClick={saveEdit}
                  {...captureProps(saveEdit)}
                  disabled={!editTag.trim()}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="atl-btn-cancel"
                  onClick={cancelEdit}
                >
                  Cancel
                </button>
              </div>
            );
          }
          return (
            <div key={tag.id} className="atl-row">
              <span
                className={`atl-val${canEdit ? ' atl-val-editable' : ''}`}
                onClick={canEdit ? () => startEdit(tag) : undefined}
              >
                {tag.tag_value}
              </span>
              {!readOnly && (
                <button
                  type="button"
                  className="atl-del"
                  onClick={() => onDelete(tag.id)}
                  aria-label="Delete"
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
        {!readOnly && (
          <div className="atl-add">
            <input
              type="text"
              className="atl-input atl-input-grow"
              placeholder="Tag value"
              value={newTag}
              onChange={e => setNewTag(e.target.value)}
            />
            <button type="button" className="atl-btn-add" onClick={onAdd} {...captureProps(onAdd)}>
              Add
            </button>
          </div>
        )}
      </div>
    </>
  );
}
